import { useEffect, useState } from "react";
import ExperienceJournal from "@/components/ExperienceJournal";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import Navbar from "@/components/Navbar";
import MistOrb from "@/components/MistOrb";
import { ArrowRight, Loader2, Moon } from "lucide-react";

const fade = (i = 0) => ({ initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: i * 0.08 } });
const Eyebrow = ({ children }) => <p className="text-[11px] uppercase tracking-[0.22em] text-primary mb-3">{children}</p>;

function Qualities({ title, words, note, tone, testId }) {
  return (
    <motion.div {...fade(2)} className="rounded-3xl border border-border bg-card/40 p-7" data-testid={testId}>
      <Eyebrow>{title}</Eyebrow>
      <div className="flex flex-wrap gap-2 mb-4">
        {words.map((w) => <span key={w} className={`px-3.5 py-1.5 rounded-full text-sm border ${tone}`}>{w}</span>)}
      </div>
      {note && <p className="text-sm text-muted-foreground leading-relaxed">{note}</p>}
    </motion.div>
  );
}

function Moments({ moments, shifts }) {
  const items = [
    ...shifts.filter((s) => s.to).map((s) => ({ date: s.date || "", text: `Shifted “${s.from}” → “${s.to}”` })),
    ...moments,
  ].slice(0, 8);
  return (
    <motion.div {...fade(4)} className="rounded-3xl border border-border bg-card/40 p-7" data-testid="evolution-moments">
      <Eyebrow>Moments that mattered</Eyebrow>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Your story is just beginning. Every honest conversation adds a line here.</p>
      ) : (
        <ol className="relative border-l border-white/10 ml-1.5 space-y-5">
          {items.map((m, i) => (
            <li key={i} className="pl-5 relative">
              <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-primary/70" />
              {m.date && <p className="text-[11px] text-muted-foreground/70 mb-0.5">{m.date}</p>}
              <p className="text-sm leading-relaxed">{m.text}</p>
            </li>
          ))}
        </ol>
      )}
    </motion.div>
  );
}

function Change({ change }) {
  if (!change?.has_data) return null;
  return (
    <motion.div {...fade(5)} className="rounded-3xl border border-border bg-card/40 p-7" data-testid="your-change">
      <Eyebrow>Where you started → where you are</Eyebrow>
      <div className="space-y-4">
        {change.changes.map((c, i) => (
          <div key={i} className="flex items-center justify-between text-sm" data-testid={`change-${i}`}>
            <span>{c.skill}</span>
            <span className={c.delta > 0 ? "text-teal-400" : "text-muted-foreground"}>{c.delta > 0 ? "Growing" : c.delta < 0 ? "Being tested" : "Steady"} · {c.sessions} sessions</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function ProgressPage() {
  const [e, setE] = useState(null);
  const [change, setChange] = useState(null);
  useEffect(() => { api.get("/progress/evolution").then((r) => setE(r.data)).catch(() => setE(false)); }, []);
  useEffect(() => { api.get("/progress/change").then((r) => setChange(r.data)).catch(() => {}); }, []);

  if (!e) return (
    <div className="min-h-screen bg-background"><Navbar />
      <div className="flex items-center justify-center gap-2 text-muted-foreground py-32" data-testid="evolution-loading"><Loader2 className="w-4 h-4 animate-spin" /> Reading your story…</div>
    </div>
  );

  const a = e.activity || {};
  const step = e.first_step;

  return (
    <div className="min-h-screen bg-background" data-testid="evolution-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <section className="grid md:grid-cols-[1fr_auto] gap-8 items-center mb-12">
          <motion.div {...fade(0)}>
            <Eyebrow>Your Evolution</Eyebrow>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5" data-testid="evolution-chapter">{e.chapter}</h1>
            <p className="font-serif text-xl sm:text-2xl leading-relaxed text-foreground/85 max-w-xl" data-testid="evolution-story">{e.story}</p>
          </motion.div>
          <div className="hidden md:block"><MistOrb size={220} state="idle" testId="evolution-orb" /></div>
        </section>

        {e.becoming?.to && (
          <motion.div {...fade(1)} className="rounded-3xl border border-primary/30 bg-primary/[0.05] p-7 mb-6" data-testid="evolution-becoming">
            <Eyebrow>Who you're becoming</Eyebrow>
            <p className="text-sm text-muted-foreground line-through decoration-primary/40 mb-1">“{e.becoming.from}”</p>
            <p className="font-serif text-2xl sm:text-3xl text-foreground">“{e.becoming.to}”</p>
          </motion.div>
        )}

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <Qualities title="Already strong in you" words={e.strengths || []} note={e.strength_note} tone="border-teal-400/30 text-teal-200 bg-teal-400/[0.06]" testId="evolution-strengths" />
          <Qualities title="Your edges — not flaws, the next frontier" words={e.edges || []} note={e.edge_note} tone="border-white/15 text-foreground/80" testId="evolution-edges" />
        </div>

        <motion.div {...fade(3)} className="rounded-3xl border border-border bg-card/40 p-7 mb-6 flex flex-col sm:flex-row sm:items-center gap-5 justify-between" data-testid="evolution-next">
          <div>
            <Eyebrow>Lean into next</Eyebrow>
            <p className="font-serif text-xl leading-snug max-w-xl">{e.next}</p>
          </div>
          <Link to={step?.route || "/practice"} className="shrink-0" data-testid="evolution-next-btn">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90 transition-opacity">
              {step?.cta || "Practise it"} <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        </motion.div>

        {e.sleep?.total > 0 && (
          <motion.div {...fade(3)} className="rounded-3xl border border-indigo-300/20 bg-gradient-to-br from-[#0b1026] to-[#121a3a] p-7 mb-6 flex items-center gap-5" data-testid="evolution-sleep">
            <div className="relative w-16 h-16 shrink-0 flex items-center justify-center">
              <motion.span className="absolute inset-0 rounded-full bg-indigo-300/15" animate={{ scale: [0.85, 1.1, 0.85], opacity: [0.5, 1, 0.5] }} transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }} />
              <Moon className="w-6 h-6 text-indigo-200 relative" />
            </div>
            <div>
              <Eyebrow>Rest</Eyebrow>
              <p className="font-serif text-2xl text-white" data-testid="evolution-sleep-streak">
                {e.sleep.streak > 1 ? `${e.sleep.streak} nights in a row resting with Xcelio` : e.sleep.streak === 1 ? "You rested with Xcelio last night" : `${e.sleep.total} night${e.sleep.total > 1 ? "s" : ""} rested with Xcelio`}
              </p>
              <p className="text-sm text-white/60 mt-1">{e.sleep.streak > 1 ? "Your nervous system is learning that it's safe to let go." : "Every night you let yourself rest, something softens."}</p>
            </div>
          </motion.div>
        )}

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <Moments moments={e.moments || []} shifts={e.inner_shifts || []} />
          <Change change={change} />
        </div>

        <ExperienceJournal />

        <p className="text-xs text-muted-foreground text-center" data-testid="evolution-activity">
          {a.conversations_practised || 0} practised · {a.real_world_conversations || 0} real conversations · {a.missions_completed || 0} missions · {e.streak || 0}-day rhythm
        </p>
      </main>
    </div>
  );
}
